import { useState, useEffect, useRef } from "react";
import { Button, Form, Icon, Modal, Popup, Table } from "semantic-ui-react";
import { Trash, Eye } from 'phosphor-react';
import { toast } from 'react-toastify';
import moment from "moment";

import { ExpensesService } from "../../services/methods/ExpensesService";
import { OsService } from "../../services/methods/OsService";

import { useSchedule } from "../../hooks/schedules";
import { useUser } from "../../hooks/user";

import { IExpenseResponse, ISchedule } from "../../interfaces/ISchedule";
import { IExpensesData } from "../../interfaces/IExpenses";

import { getBase64 } from "../../utils/getBase64";
import { styleSheet } from "../../assets/styles/global";

import {
  Title,
  StyledInput,
  OutgoingContainer,
  TableColumnText,
  TableDataText,
  ButtonTable,
  StyledModal,
  InputLabel,
  SelectContainer,
  PrimaryButton,
  SecondaryButton,
  DelicatePlusButton,
  RowContainer,
  FieldWrapper,
  TypeWrapper,
  ValueWrapper,
  FileWrapper,
  TableContainer
} from './styles'

interface Props {
  visible: boolean;
  closeModal: () => void;
  data: ISchedule;
}

interface IOptions {
  cod: string;
  descr: string;
  qtdmax: string;
  valmax: string;
}

interface IExpense {
  id?: number;
  vlr: number;
  cod: string;
  img?: string;
  imgName?: string;
}

interface IProjeto {
  tiposdesp: {
    cod: string;
    descr: string;
    qtdmax: string;
    valmax: string;
  }
}

export const OutgoingOSModal = ({ visible, closeModal, data }: Props) => {
  const expensesService: ExpensesService = new ExpensesService();
  const osService: OsService = new OsService();

  const [options, setOptions] = useState<IOptions[]>([])
  const [option, setOption] = useState('SELECIONE')
  const [value, setValue] = useState('')
  const [file, setFile] = useState('')
  const [fileName, setFileName] = useState('')
  const [values, setValues] = useState({ val: [{} as IExpense] });
  const [isFirstExpense, setIsFirstExpense] = useState(true);
  const [loadExpense, setLoadExpense] = useState(false);
  const [loadOptions, setLoadOptions] = useState(false);

  const inputRef = useRef<HTMLInputElement>({} as HTMLInputElement);

  const {
    updateSchedulesData,
    setReceiveIsUpdate,
    isVisualOutgoingOSModal,
    setIsLoading,
    analyst
  } = useSchedule()

  const { userInfo } = useUser();

  function createInputs() {
    if (isFirstExpense) return

    return (
      <OutgoingContainer>
        <Title>Despesas Lançadas</Title>
        <TableContainer>
          <Table celled compact sortable selectable striped unstackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  <TableColumnText>Tipo</TableColumnText>
                </Table.HeaderCell>
                <Table.HeaderCell>
                  <TableColumnText>Valor</TableColumnText>
                </Table.HeaderCell>
                <Table.HeaderCell>
                  <TableColumnText>Comprovante</TableColumnText>
                </Table.HeaderCell>
                <Table.HeaderCell width={1}>
                  <TableColumnText>Opção</TableColumnText>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {values.val.map((el, i) => (
                <Table.Row key={i}>
                  <Table.Cell><TableDataText>{options.find(option => option.cod === el.cod)?.descr}</TableDataText></Table.Cell>
                  <Table.Cell><TableDataText>R$ {el.vlr}</TableDataText></Table.Cell>
                  <Table.Cell><TableDataText>{el.imgName ? el.imgName : 'Nenhum Arquivo Selecionado'}</TableDataText></Table.Cell>
                  <Table.Cell width={1}>
                    <TableDataText>
                      <div style={{ display: 'flex' }}>
                        <Popup
                          trigger={
                            <ButtonTable onClick={() => removeClick(i)}>
                              <Trash size={20} color="#E47261" />
                            </ButtonTable>
                          }
                          content={<span style={{ fontSize: '1.3rem' }}>Excluir</span>}
                          inverted
                        />
                        {el.imgName &&
                          <Popup
                            trigger={
                              <ButtonTable onClick={() => handleViewPhoto(i)}>
                                <Eye size={20} color="#6FCCF1" />
                              </ButtonTable>
                            }
                            content={<span style={{ fontSize: '1.3rem' }}>Visualizar</span>}
                            inverted
                          />
                        }
                      </div>
                    </TableDataText>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </TableContainer>
      </OutgoingContainer>
    )
  }

  const refreshParentData = async () => {
    try {
      const token = await sessionStorage.getItem('token')
      const analista = (userInfo.cargo === 'C' && analyst !== "") ? analyst : userInfo.codigo
      const { data: dataResponse } = await osService.getOs(data.key, analista, token!)
      if (dataResponse.status === 'ok') {
        await updateSchedulesData(dataResponse.analista)
      }
    } catch (error) {
      console.log('Error refreshing parent data', error)
    }
  }

  const handleAddnewExpense = async () => {
    if (option === 'SELECIONE') {
      return toast.error('Selecione um tipo')
    }
    if (value === '' || value === '0') {
      return toast.error('O campo valor é obrigatorio.')
    }
    const maxVal = options.find(opt => opt.cod.includes(option))?.valmax
    const maxQtd = options.find(opt => opt.cod.includes(option))?.qtdmax
    if (Number(value) > Number(maxVal!)) {
      return toast.error(`O limite para esse tipo de despesa é R$${maxVal}`)
    }

    // Check max quantity (this check is now complex because we need to count EXISTING expenses too which are in 'existents' state?)
    // Assuming API or loadExpenseData manages full list.
    // Ideally we should check against 'existents' + 'newly added' if we don't reload.
    // If we reload, checking 'existents' is enough.
    // Assuming `existents` is the state for loaded expenses. 
    // Wait, let's look at `loadExpenseData` later. For now, trust the API will reject or we reload.

    try {
      const token = await sessionStorage.getItem('token');
      const payload = {
        os: data.os,
        despesas: [{
          cod: option,
          vlr: Number(value),
          img: file,
          imgName: fileName
        }]
      };

      const response = await expensesService.launchExpense(token, payload);

      if (response.data.status === 'ok') {
        toast.success(response.data.msg);
        // Refresh list
        loadExpenseData();
        // Refresh parent data (totals on main screen)
        refreshParentData();

        // Clear inputs
        setValue('')
        setFile('')
        setFileName('')
        setOption('SELECIONE')
      } else {
        // Handle 409 or other errors handled by service/provider? 
        // Service returns response even on error usually if caught.
        // If status is not ok (e.g. 409 Conflict logic inside service might return response.data)
        // Backend returns 409 with { status: 'error', msg: ... }
        if (response.data.msg) toast.error(response.data.msg);
        else toast.error('Erro ao incluir despesa.');
      }

    } catch (error: any) {
      if (error.response && error.response.data) {
        toast.error(error.response.data.msg || 'Erro ao incluir despesa.');
      } else {
        toast.error('Erro desconhecido ao incluir despesa.');
      }
    }
  }

  const removeClick = async (index: number) => {
    const vals = [...values.val];
    const itemToDelete = vals[index];

    if (itemToDelete.id) {
      try {
        const token = await sessionStorage.getItem('token');
        const response = await expensesService.deleteExpense(token, itemToDelete.id);

        if (response.data.status === 'ok') {
          toast.success(response.data.msg);
          loadExpenseData(); // Refresh list
          refreshParentData(); // Refresh parent totals
        } else {
          toast.error(response.data.msg || 'Erro ao excluir despesa.');
        }
      } catch (error: any) {
        if (error.response && error.response.data) {
          toast.error(error.response.data.msg || 'Erro ao excluir despesa.');
        } else {
          toast.error('Erro desconhecido ao excluir.');
        }
      }
    } else {
      // Fallback for items without ID (should not happen in new flow)
      vals.splice(index, 1);
      setValues({ val: vals });
    }
  }

  const handleViewPhoto = async (index: number) => {
    const vals = [...values.val];
    const despesa = vals[index];

    if (!despesa.id) {
      return toast.error('Despesa sem ID para buscar imagem.');
    }

    try {
      const token = await sessionStorage.getItem('token');
      const response = await expensesService.getExpenseImage(token, despesa.id);

      if (response.data.status === 'ok') {
        const imgData = response.data.img;
        if (!imgData) {
          return toast.warning('Nenhuma imagem encontrada para esta despesa.');
        }

        const newTab = window.open();
        const isPdf = imgData.includes('application/pdf');

        let content = '';
        if (isPdf) {
          content = `<embed src="${imgData}" width="100%" height="100%" type="application/pdf" />`;
        } else {
          content = `<img src="${imgData}" style="max-width: 100%;" />`;
        }

        newTab?.document.write(
          `<!DOCTYPE html>
            <html style="height: 100%; width: 100%;">
                <head>
                    <title>${response.data.name || despesa.imgName}</title>
                    <style>body { margin: 0; padding: 0; height: 100%; width: 100%; display: flex; align-items: center; justify-content: center; background-color: #f0f0f0; }</style>
                </head>
                <body>
                    ${content}
                </body>
            </html>`
        );
        newTab?.document.close();
      } else {
        toast.error(response.data.msg || 'Erro ao buscar imagem.');
      }

    } catch (error) {
      console.error('Erro ao visualizar imagem:', error);
      toast.error('Erro ao buscar imagem.');
    }
  }



  async function loadProjectExpenseData() {
    setLoadOptions(true);
    try {
      const token = await sessionStorage.getItem('token')

      const response = await expensesService.getProjectExpense(token, data.codpro)

      if (response.data.status === 'ok') {
        const arrayOptions = [] as IOptions[];
        response.data.projeto.map((pro: IProjeto) => {
          arrayOptions.push({
            cod: pro.tiposdesp.cod,
            descr: pro.tiposdesp.descr,
            qtdmax: pro.tiposdesp.qtdmax,
            valmax: pro.tiposdesp.valmax
          })
          return pro
        })
        setOptions(arrayOptions)
      } else {
        toast.error(response.data.msg)
      }
    } catch (error: any) {
      if (error.response) {
        toast.error(error.response.data.errorMessage)
      }
      console.log(error)
    } finally {
      setLoadOptions(false);
    }
  }

  async function loadExpenseData() {
    try {
      const token = await sessionStorage.getItem('token')

      const response = await expensesService.getExpense(token, data.os)

      if (response.data.status === 'ok') {
        console.log(response)

        setValues({
          val: response.data.dados.map((despesa: any) => {
            return {
              id: despesa.id,
              vlr: despesa.valor,
              cod: despesa.despesa,
              img: despesa.dataImage,
              imgName: despesa.nameImage,
            }
          })
        })
        setIsFirstExpense(false)
      } else {
        toast.error(response.data.msg)
      }
    } catch (error: any) {
      if (error.response) {
        if (error.response.data.errorMessage.includes('registro informado não encontrado')) {
          return
        } else {
          toast.error(error.response.data.errorMessage)
        }
      }
    } finally {
      setLoadExpense(false);
    }
  }

  async function handleSetFiles(files: FileList | null) {
    var filesArr = Array.prototype.slice.call(files);
    const fileResponse: string = await getBase64(filesArr[0]);

    setFileName(filesArr[0].name);
    setFile(fileResponse);
  }

  useEffect(() => {
    if (!isVisualOutgoingOSModal) {
      loadProjectExpenseData();
      loadExpenseData();
    }
  }, [])

  return (
    <StyledModal
      open={visible}
      closeIcon
      onClose={closeModal}
      size="large"
      closeOnDimmerClick={false}
    >
      <Modal.Header style={{ color: '#54278b', fontSize: '1.8rem', fontWeight: 'bold' }}>
        {data.nomecli} - {data.projeto} {moment(data.data).format('DD/MM/YYYY')}
      </Modal.Header>

      <Modal.Content scrolling style={{ width: '100%', minHeight: '400px', padding: '3rem' }}>

        <Title>Lançamento de Despesa</Title>

        <RowContainer style={{ marginBottom: '3rem' }}>
          <TypeWrapper>
            <InputLabel>Tipo</InputLabel>
            <SelectContainer
              fluid
              placeholder="Selecione o tipo"
              options={options.map(option => { return { key: option.cod, text: option.descr, value: option.cod } })}
              onChange={(ev: any, { value }: any) => setOption(String(value))}
              value={option}
              loading={loadOptions}
            />
          </TypeWrapper>

          <ValueWrapper>
            <InputLabel>Valor</InputLabel>
            <StyledInput
              type="number"
              placeholder="0,00"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </ValueWrapper>
          <FileWrapper>
            <SecondaryButton onClick={() => inputRef.current.click()}>
              <Icon name='cloud' style={{ marginRight: '0.5rem' }} />
              {file ? 'Arquivo Anexado' : 'Anexar Comprovante'}
              <input ref={inputRef} type="file" style={{ display: 'none' }} onChange={(e) => handleSetFiles(e.target.files)} />
            </SecondaryButton>
          </FileWrapper>

          <DelicatePlusButton onClick={handleAddnewExpense}>
            <Icon name='plus' style={{ margin: 0 }} />
          </DelicatePlusButton>
        </RowContainer>

        {createInputs()}

      </Modal.Content>


    </StyledModal>
  )
}
